import {
  Drawer,
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
} from "@/components/ui/drawer";
import { Heading } from "@/components/ui/heading";
import { CloseIcon, Icon, InfoIcon } from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import { useBreakpointValue } from "@/components/ui/utils/use-break-point-value";
import { VStack } from "@/components/ui/vstack";
import {
  OrgTransactionResponseDTODataItem,
  TransactionResponseDTODataItem,
  TransactionResponseDTODataItemMetadataFeedback,
} from "@/generated/api/models";
import { getName } from "@/utils/name";
import { useMobileBreakpoint } from "@/utils/useMobileBreakpoint";
import { useTransactionInfo } from "@/utils/useTransactionInfo";
import { CoinsIcon, Wallet } from "lucide-react-native";
import { ComponentPropsWithoutRef, useMemo } from "react";
import { SafeAreaView } from "@/components/ui/safe-area-view";
import {
  TransactionBalanceChangeItem,
  TransactionBalanceChangeItemProps,
} from "./transaction.item";
import { Alert, AlertIcon, AlertText } from "@/components/ui/alert";

export function TransactionModal({
  transaction,
  isOpen,
  onClose,
}: {
  transaction:
    | OrgTransactionResponseDTODataItem
    | TransactionResponseDTODataItem;
} & Pick<ComponentPropsWithoutRef<typeof Drawer>, "isOpen" | "onClose">) {
  const size: "sm" | "md" | "full" = useBreakpointValue({
    default: "full",
    md: "md",
    lg: "md",
  });

  const breakpoint = useMobileBreakpoint();
  const { getDetails, formatCents, dateFormatter } =
    useTransactionInfo(transaction);
  const mainDetails = useMemo(() => getDetails(transaction), [transaction]);

  const dateText = dateFormatter.format(new Date(transaction.date as string));

  const balanceChanges: TransactionBalanceChangeItemProps[] = useMemo(() => {
    if ("items" in transaction) {
      return transaction.items.flatMap((i) => {
        return i.transactions.map<TransactionBalanceChangeItemProps>((t) => {
          const details = getDetails(t);
          let name = details.name;
          let icon: TransactionBalanceChangeItemProps["icon"] = details.icon;
          let subtitle = "";
          let feedback:
            | TransactionResponseDTODataItemMetadataFeedback
            | undefined;

          if (i.participant) {
            name = getName(i.participant.user);
            icon = {
              fallbackName: name,
              src: i.participant.user.avatarImage ?? undefined,
            };
            if (i.qr) {
              subtitle = `QR-code: ${i.qr.internalCode}`;
            }
            feedback =
              t.metadata?.feedback &&
              t.metadata.feedback.participantId === i.participant.id
                ? t.metadata.feedback
                : undefined;
          } else if (i.wallet) {
            name = i.wallet.name;
            icon = Wallet;
            if (i.qr) {
              subtitle = `QR-code: ${i.qr.internalCode}`;
            }
            feedback =
              t.metadata?.feedback && !t.metadata.feedback.participantId
                ? t.metadata.feedback
                : undefined;
          } else {
            if (i.qr) {
              subtitle = `QR-code: ${i.qr.internalCode}`;
            }
            feedback = t.metadata?.feedback;
          }

          return {
            id: t.id,
            name,
            amount: details.amount,
            subtitle,
            icon,
            pooled: t.metadata?.pooled,
            feedback,
            formatCents,
            transaction: t,
          };
        });
      });
    } else {
      return [
        {
          id: transaction.id,
          name: mainDetails.name,
          amount: mainDetails.amount,
          icon: mainDetails.icon,
          feedback: transaction.metadata?.feedback,
          pooled: transaction.metadata?.pooled,
          formatCents,
          transaction,
        },
      ];
    }
  }, [transaction]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      anchor={breakpoint === "desktop" ? "right" : "bottom"}
    >
      <DrawerBackdrop />
      <DrawerContent>
        <SafeAreaView>
          <DrawerHeader>
            <Heading size="lg">{mainDetails.name}</Heading>
            <DrawerCloseButton className="min-w-9 min-h-9 items-center justify-center">
              <Icon
                as={CloseIcon}
                size="md"
                className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
              />
            </DrawerCloseButton>
          </DrawerHeader>
          <DrawerBody>
            <VStack className="gap-8">
              <VStack className="gap-1">
                <Heading size="md">{formatCents(mainDetails.amount)}</Heading>
                <Text size="sm">{dateText}</Text>
                {!!(transaction.metadata as any)?.organization?.name && (
                  <Text size="sm">
                    {(transaction.metadata as any)?.organization?.name}
                  </Text>
                )}
              </VStack>
              <VStack className="gap-2">
                {mainDetails.category !== undefined &&
                  mainDetails.category === "payout" && (
                    <>
                      {transaction.metadata &&
                        "payoutFee" in transaction.metadata &&
                        !!transaction.metadata.payoutFee && (
                          <Alert action="muted" variant="solid">
                            <AlertIcon as={CoinsIcon} className="min-w-5" />
                            <AlertText>
                              Fee for bank transfer:{" "}
                              {formatCents(transaction.metadata.payoutFee)}
                            </AlertText>
                          </Alert>
                        )}

                      <Alert action="info" variant="solid">
                        <AlertIcon as={InfoIcon} className="min-w-5" />
                        <AlertText>
                          Usually we execute payouts instantly, however delays
                          may happen sometimes on provider and bank side. In
                          case if you haven't received funds on your account
                          within 3 business days, please contact support. Please
                          note, that Hoteza Tips may appear as{" "}
                          <AlertText bold>"MAYSKAYA LAB LTD"</AlertText> or{" "}
                          <AlertText bold>"GoTips"</AlertText> in account
                          statement.
                        </AlertText>
                      </Alert>
                    </>
                  )}
              </VStack>

              <VStack className="gap-4">
                <Heading size="md">Balance changes</Heading>
                {balanceChanges.map((b) => (
                  <TransactionBalanceChangeItem key={b.id} {...b} />
                ))}
              </VStack>
            </VStack>
          </DrawerBody>
        </SafeAreaView>
      </DrawerContent>
    </Drawer>
  );
}
